/**
 * One-shot marker for the service enquiry at `/get-started` — the conversion
 * that matters most, since the $299/mo service is the primary offer and the
 * enquiry is the only place someone commits to it.
 *
 * The enquiry action redirects to a fixed thanks page, so unlike signup there
 * IS a single URL we could tag. The marker is still the right mechanism: the
 * thanks URL is shareable, bookmarkable and reachable by back-navigation, and
 * any of those would count a lead nobody submitted. See `conversion-marker.ts`.
 *
 * Claimed in `root.tsx` beside the signup conversion rather than on the thanks
 * page itself, so a gtag that had not finished loading on the first paint after
 * the redirect gets retried on the next navigation instead of losing the
 * conversion outright.
 */
import {
	clearConversionMarker,
	conversionMarkerCookie,
	readConversionMarker,
	readConversionMarkerFromRequest,
} from './conversion-marker.ts'

export const ENQUIRY_CONVERSION_COOKIE = 'cb_enquiry_conversion'

/**
 * `Set-Cookie` value to append to the enquiry redirect (server side).
 *
 * `enquiryId` is the reference printed in the notification email, so a
 * conversion in Google Ads and a lead in the fulfilment inbox can be matched up
 * by hand while there is no lead table to join them.
 */
export function enquiryConversionCookie(enquiryId: string): string {
	return conversionMarkerCookie(ENQUIRY_CONVERSION_COOKIE, enquiryId)
}

/** The enquiry id this browser just submitted, or null (client side). */
export function readEnquiryConversionMarker(): string | null {
	return readConversionMarker(ENQUIRY_CONVERSION_COOKIE)
}

/** Same, from a loader's request (server side, races nothing). */
export function readEnquiryConversionMarkerFromRequest(
	request: Request,
): string | null {
	return readConversionMarkerFromRequest(request, ENQUIRY_CONVERSION_COOKIE)
}

/** Removes the marker. Call only once the conversion actually fired. */
export function clearEnquiryConversionMarker(): void {
	clearConversionMarker(ENQUIRY_CONVERSION_COOKIE)
}
